import {
  AUTOMATION_SEQUENCE_STEP_TYPES,
  getSignedClassRelativeTriggerOffsetSeconds,
  getStepDurationSeconds,
  getStepStartOffsetSeconds,
} from "./automationSequenceModel";
import {
  buildDateFromActiveDate,
  getScheduleRuntimeMode,
  resolveAutomationStartTimesForCollection,
} from "./automationTriggerResolver";
import { getMusicRuntimeEventEndTime } from "./autoMusicRoutineSegment";
import { findPreviousClassMeetingDate } from "./classMeetingDateNavigation";
import { formatShortDate, isBirthdayOnDate, normalizeToLocalDate } from "./dateTime";
import {
  formatDateKey,
  formatLongDisplayDate,
  getCalendarOnlyClassBlocksForDate,
  resolveScheduleForDate,
} from "./scheduleResolver";

export const SEQUENCE_RUNTIME_RECENT_DUE_MS = 30000;
export const SEQUENCE_RUNTIME_HISTORY_LIMIT = 80;

export const resolveRuntimeUseClassStyleDefaults = (
  config = {},
  { defaultWhenUnset = true } = {}
) => {
  if (config?.useClassStyleDefaults === false) return false;
  if (config?.useClassStyleDefaults === true) return true;
  return defaultWhenUnset;
};

export const AUTOMATION_RUNTIME_REASONS = {
  ACTIVE_DATE_MISMATCH: "Blocked: Active Date does not match today's date.",
  AUTOMATIONS_PAUSED: "Blocked: Automations are paused for this date.",
  NO_SCHOOL_WARNING: "Warning: This date is marked No School on your schedule.",
  PLAYLIST_NOT_FOUND: "Skipped: Playlist not found.",
  NO_CLASS_AT_CUSTOM_TIME: "Skipped: No class in session at this time.",
  NO_BIRTHDAY_STUDENTS: "Skipped: No birthday students found.",
  MUSIC_ALREADY_PLAYING: "Skipped: Music is already playing.",
  MUSIC_TOO_LATE: "Skipped: Not enough time remaining in this music segment.",
  MUSIC_STOPPED_BY_NEXT_ROUTINE: "Stopped: Replaced by the next Auto Music routine.",
  HIDDEN_TAB: "Skipped: Browser tab is inactive.",
  MISSING_CLASS_IN_SCHEDULE: "Skipped: Class is not in today's schedule.",
  MUSIC_PLAYBACK_STARTED: "Music audio playback started.",
  MUSIC_PLAYBACK_STARTED_SILENT: "Music audio started at zero volume.",
  MUSIC_PLAYBACK_BLOCKED: "Browser blocked music audio playback.",
  MUSIC_PLAYBACK_FAILED: "Music audio playback failed.",
  MUSIC_AUDIO_LOAD_ERROR: "Music audio file failed to load.",
  MUSIC_VOLUME_MUTED: "Music volume is muted or set to zero.",
  MUSIC_YOUTUBE_AUTOPLAY_BLOCKED: "Browser blocked YouTube autoplay.",
  MUSIC_YOUTUBE_PLAYER_ERROR: "YouTube player reported a playback error.",
  MUSIC_MISSING_URL: "Music track URL is missing.",
};

/** Fired when schedule-derived routine event keys disappear (class time change, etc.). */
export const AUTOMATION_SEQUENCE_INVALIDATE_EVENTS = "automation-sequence:invalidate-events";

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatSequenceRuntimeDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const formatSequenceRuntimeTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const toIso = (date) => (date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : "");

const getSequenceTriggerFields = (sequence = {}) => {
  const trigger = sequence?.trigger || {};
  return {
    triggerMode: trigger.triggerMode,
    schedule: trigger.schedule,
    dateRules: trigger.dateRules,
    relativeConfig: trigger.relativeConfig,
  };
};

export const toRuntimeResolverItem = (sequence = {}) => ({
  ...sequence,
  ...getSequenceTriggerFields(sequence),
  active: sequence?.status !== "paused",
  sequence,
});

export const getSequenceEventKey = ({
  sequenceId,
  stepId,
  classId,
  anchorTime,
  startOffsetSeconds = 0,
  durationSeconds = 1,
  triggerOffsetSeconds = 0,
  includeClassInKey = true,
}) =>
  [
    sequenceId || "sequence",
    stepId || "step",
    includeClassInKey ? (classId || "no-class") : "no-class",
    anchorTime instanceof Date ? anchorTime.toISOString() : String(anchorTime || ""),
    `start:${Number(startOffsetSeconds) || 0}`,
    `duration:${Math.max(1, Number(durationSeconds) || 1)}`,
    `trigger:${Number(triggerOffsetSeconds) || 0}`,
  ].join("|");

export const getSequenceEventTimingStatus = (event = {}, nowInput = new Date()) => {
  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  const startMs = event.startTime?.getTime?.();
  const endMs = event.endTime?.getTime?.();
  const nowMs = now.getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(nowMs)) {
    return { status: "invalid", reason: "This step has an invalid start time." };
  }

  if (event.type === AUTOMATION_SEQUENCE_STEP_TYPES.WAIT) {
    if (nowMs < startMs) return { status: "scheduled", reason: "This wait step has not started yet." };
    return { status: "noop", reason: "Wait steps only shape the routine timeline." };
  }

  if (nowMs < startMs) {
    return { status: "scheduled", reason: "This step is scheduled later today." };
  }

  if (Number.isFinite(endMs) && nowMs <= endMs) {
    return { status: "active", reason: "This step is inside its scheduled active window." };
  }

  if (nowMs - startMs <= SEQUENCE_RUNTIME_RECENT_DUE_MS) {
    return { status: "recentlyDue", reason: "This step was recently due and is still in the catch-up window." };
  }

  return { status: "missed", reason: "This step is outside the catch-up window." };
};

export const getRemainingStepDurationSeconds = (event = {}, nowInput = new Date()) => {
  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  const endMs = event.endTime?.getTime?.();
  if (!Number.isFinite(endMs)) return Math.max(1, Number(event.durationSeconds) || 1);
  return Math.max(1, Math.ceil((endMs - now.getTime()) / 1000));
};

export const getRemainingResourceAutoCloseSeconds = (event = {}, nowInput = new Date()) => {
  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  const startMs = event.startTime?.getTime?.();
  const durationSeconds = Math.max(
    1,
    Number(event.config?.duration) || Number(event.durationSeconds) || 1
  );
  const elapsedSeconds = Number.isFinite(startMs)
    ? Math.max(0, (now.getTime() - startMs) / 1000)
    : 0;

  return Math.max(1, Math.ceil(durationSeconds - elapsedSeconds));
};

export const shouldExecuteSequenceEvent = ({ event, now = new Date(), firedKeys = new Set() }) => {
  if (!event || firedKeys.has(event.key)) return false;
  const timing = getSequenceEventTimingStatus(event, now);
  return timing.status === "active" || timing.status === "recentlyDue";
};

export const getBirthdayStudentsForDate = (classItem = {}, activeDateInput = new Date()) => {
  const activeDate = buildDateFromActiveDate(activeDateInput);
  const roster = Array.isArray(classItem?.rosterData?.roster) ? classItem.rosterData.roster : [];

  return roster.filter((student) => {
    if (!student?.birthday) return false;
    return isBirthdayOnDate(student.birthday, activeDate);
  });
};

const addDays = (dateInput, daysToAdd = 0) => {
  const date = normalizeToLocalDate(dateInput);
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

const getDateRangeInclusive = (startDateInput, endDateInput) => {
  const startDate = normalizeToLocalDate(startDateInput);
  const endDate = normalizeToLocalDate(endDateInput);
  if (startDate > endDate) return [];

  const dates = [];
  const pointer = new Date(startDate);
  while (pointer <= endDate) {
    dates.push(new Date(pointer));
    pointer.setDate(pointer.getDate() + 1);
  }
  return dates;
};

const getBirthdayRelativeDateLabel = (birthdayDate, activeDate) => {
  const birthdayLocalDate = normalizeToLocalDate(birthdayDate);
  const activeLocalDate = normalizeToLocalDate(activeDate);
  const diffDays = Math.round((activeLocalDate.getTime() - birthdayLocalDate.getTime()) / DAY_MS);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return birthdayLocalDate.toLocaleDateString("en-US", { weekday: "long" });
  }
  return formatShortDate(birthdayLocalDate);
};

export { findPreviousClassMeetingDate };

export const getBirthdayStudentsForClassMeetingWindow = ({
  classItem = {},
  classId = classItem?.classId,
  userSettings = {},
  activeDate: activeDateInput = new Date(),
  maxLookbackDays = 14,
} = {}) => {
  const activeDate = buildDateFromActiveDate(activeDateInput);
  const resolvedClassId = classId || classItem?.classId;
  const roster = Array.isArray(classItem?.rosterData?.roster) ? classItem.rosterData.roster : [];

  if (!roster.length || !resolvedClassId) return [];

  const activeClassBlocks = getCalendarOnlyClassBlocksForDate({
    classId: resolvedClassId,
    userSettings,
    activeDate,
  }).blocks;
  if (!activeClassBlocks.length) return [];

  const previousClassDate = findPreviousClassMeetingDate({
    classId: resolvedClassId,
    userSettings,
    activeDate,
    maxLookbackDays,
  });
  const startDate = previousClassDate ? addDays(previousClassDate, 1) : activeDate;
  const datesToCheck = getDateRangeInclusive(startDate, activeDate);

  return roster.flatMap((student) => {
    if (!student?.birthday) return [];

    const birthdayDate = datesToCheck.find((date) => isBirthdayOnDate(student.birthday, date));
    if (!birthdayDate) return [];

    return [{
      ...student,
      birthdayDate,
      birthdayDateKey: formatDateKey(birthdayDate),
      birthdayDateLabel: getBirthdayRelativeDateLabel(birthdayDate, activeDate),
    }];
  });
};

const buildSequenceBlockers = ({
  sequences = [],
  classes = [],
  resolvedSchedule,
  userSettings = {},
  systemDate = new Date(),
  scheduleRuntimeMode = "calendar",
}) => {
  if (!Array.isArray(sequences) || sequences.length === 0) {
    return [];
  }

  const blockers = [];
  const activeDateKey = resolvedSchedule?.dateKey;
  const systemDateKey = formatDateKey(systemDate);

  if (activeDateKey && activeDateKey !== systemDateKey) {
    const activeDateLabel = formatLongDisplayDate(activeDateKey);
    blockers.push({
      id: "active-date-mismatch",
      level: "blocked",
      reason: AUTOMATION_RUNTIME_REASONS.ACTIVE_DATE_MISMATCH,
      message: activeDateLabel
        ? `Automations are paused because your Active Date is currently set to ${activeDateLabel}.`
        : "Automations are paused because your Active Date does not match today.",
    });
  }

  if (userSettings?.alteredSchedule) {
    blockers.push({
      id: "altered-schedule",
      level: "blocked",
      reason: AUTOMATION_RUNTIME_REASONS.AUTOMATIONS_PAUSED,
      message: "Automations are paused for this date.",
    });
  }

  if (resolvedSchedule?.isNoSchool) {
    blockers.push({
      id: "no-school",
      level: "warning",
      reason: AUTOMATION_RUNTIME_REASONS.NO_SCHOOL_WARNING,
      message: "This date is marked No School on your schedule.",
    });
  }

  if (!resolvedSchedule?.template) {
    blockers.push({
      id: "no-template",
      level: resolvedSchedule?.isAutomationPaused ? "blocked" : "warning",
      reason: resolvedSchedule?.isAutomationPaused
        ? AUTOMATION_RUNTIME_REASONS.AUTOMATIONS_PAUSED
        : "Warning: No runnable Daily Schedule is assigned for this date.",
      message: `No runnable Daily Schedule is assigned for today (${resolvedSchedule?.label || "No Schedule"}).`,
    });
  } else if (resolvedSchedule?.isAutomationPaused) {
    blockers.push({
      id: "schedule-paused",
      level: "blocked",
      reason: AUTOMATION_RUNTIME_REASONS.AUTOMATIONS_PAUSED,
      message: "Automations are paused for this date.",
    });
  }

  const scheduledClassIds = new Set(
    (resolvedSchedule?.template?.classes || []).map((classItem) => String(classItem?.classId))
  );

  sequences.forEach((sequence) => {
    if (sequence?.status === "paused") {
      blockers.push({
        id: `${sequence.id}-paused`,
        level: "info",
        sequenceId: sequence.id,
        message: `${sequence.name || "Automated routine"} is paused.`,
      });
      return;
    }

    if (scheduleRuntimeMode === "calendar" && sequence?.trigger?.triggerMode === "fixed") {
      blockers.push({
        id: `${sequence.id}-fixed-trigger`,
        level: "blocked",
        sequenceId: sequence.id,
        reason: "Blocked: Fixed routine triggers are not supported in calendar mode.",
        message: `${sequence.name || "Automated routine"} uses an older Scheduled Routine trigger. It will not run in calendar mode. Open the routine and choose Start of Class, End of Class, or Custom Time.`,
      });
    }

    const classIds = sequence?.trigger?.relativeConfig?.classIds || [];
    if (sequence?.trigger?.triggerMode === "classRelative" && classIds.length === 0) {
      blockers.push({
        id: `${sequence.id}-no-classes`,
        level: "blocked",
        sequenceId: sequence.id,
        message: `${sequence.name || "Automated routine"} has no selected classes.`,
      });
    }

    if (!Array.isArray(sequence?.steps) || sequence.steps.length === 0) {
      blockers.push({
        id: `${sequence.id}-no-steps`,
        level: "blocked",
        sequenceId: sequence.id,
        message: `${sequence.name || "Automated routine"} has no steps.`,
      });
    }

    if (resolvedSchedule?.template && sequence?.trigger?.triggerMode === "classRelative") {
      const missingClassIds = classIds.filter((classId) => !scheduledClassIds.has(String(classId)));
      missingClassIds.forEach((classId) => {
        const classItem = classes.find((candidateClass) => String(candidateClass?.classId) === String(classId));
        blockers.push({
          id: `${sequence.id}-${classId}-missing-template-class`,
          level: "warning",
          sequenceId: sequence.id,
          classId,
          reason: AUTOMATION_RUNTIME_REASONS.MISSING_CLASS_IN_SCHEDULE,
          message: `${classItem?.className || "A selected class"} is not in today's ${resolvedSchedule.label} Daily Schedule.`,
        });
      });
    }
  });

  return blockers;
};

export const buildAutomationSequenceRuntimeSnapshot = ({
  sequences = [],
  userSettings = {},
  classes = [],
  activeDate,
  now = new Date(),
  systemDate = new Date(),
}) => {
  const normalizedActiveDate = buildDateFromActiveDate(activeDate);
  const scheduleRuntimeMode = getScheduleRuntimeMode(userSettings);
  const resolvedSchedule = resolveScheduleForDate(userSettings, normalizedActiveDate);
  const runnableSequences = (Array.isArray(sequences) ? sequences : []).filter(
    (sequence) => sequence?.status !== "paused"
  );
  const resolverItems = runnableSequences.map(toRuntimeResolverItem);
  const resolution = resolveAutomationStartTimesForCollection({
    items: resolverItems,
    userSettings,
    classes,
    activeDate: normalizedActiveDate,
    scheduleRuntimeMode,
    defaultLegacyTriggerMode: "fixed",
    ignoreAutomationPause: true,
  });
  const dateWarning = resolvedSchedule?.isNoSchool
    ? "This date is marked No School on your schedule."
    : "";

  const events = resolution.triggers
    .flatMap((trigger) => {
      const sequence = trigger.item || {};
      const steps = Array.isArray(sequence?.steps) ? sequence.steps : [];
      const classId = trigger.reference?.classId || "";
      const classItem = classes.find((candidateClass) => String(candidateClass?.classId) === String(classId)) || null;

      return steps.map((step, stepIndex) => {
        const startOffsetSeconds = getStepStartOffsetSeconds(step);
        const durationSeconds = getStepDurationSeconds(step);
        const signedTriggerOffsetSeconds = trigger.triggerMode === "classRelative"
          ? getSignedClassRelativeTriggerOffsetSeconds(sequence.trigger)
          : 0;
        const anchorTime = new Date(
          trigger.startTime.getTime() - signedTriggerOffsetSeconds * 1000
        );
        const startTime = new Date(anchorTime.getTime() + startOffsetSeconds * 1000);
        const endTime = step.type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC
          ? getMusicRuntimeEventEndTime({
            step,
            startTime,
            durationSeconds,
            activeDate: normalizedActiveDate,
          })
          : step.type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE
            ? startTime
            : new Date(startTime.getTime() + Math.max(1, durationSeconds) * 1000);
        const key = getSequenceEventKey({
          sequenceId: sequence.id,
          stepId: step.id || `step-${stepIndex}`,
          classId,
          anchorTime,
          startOffsetSeconds,
          durationSeconds,
          triggerOffsetSeconds: signedTriggerOffsetSeconds,
          includeClassInKey: trigger.triggerMode !== "dateRules",
        });
        const timing = getSequenceEventTimingStatus({ type: step.type, startTime, endTime }, now);

        return {
          key,
          dateKey: formatDateKey(normalizedActiveDate),
          sequenceId: sequence.id,
          sequenceName: sequence.name || "Automated routine",
          stepId: step.id || `step-${stepIndex}`,
          stepIndex,
          type: step.type,
          title: step.title || step.summary || step.type,
          config: step.config || {},
          startOffsetSeconds,
          durationSeconds,
          anchorTime,
          startTime,
          endTime,
          classId,
          className: trigger.reference?.className || classItem?.className || "",
          classItem,
          scheduleSource: trigger.scheduleSource,
          triggerMode: trigger.triggerMode,
          reference: trigger.reference,
          status: timing.status,
          statusReason: timing.reason,
          warning: dateWarning,
        };
      });
    })
    .sort((leftEvent, rightEvent) => leftEvent.startTime.getTime() - rightEvent.startTime.getTime());

  const nextEvents = events.filter((event) => event.startTime.getTime() >= now.getTime()).slice(0, 8);
  const activeEvents = events.filter((event) =>
    ["active", "recentlyDue"].includes(getSequenceEventTimingStatus(event, now).status)
  );

  return {
    generatedAt: now,
    scheduleRuntimeMode,
    activeDate: normalizedActiveDate,
    activeDateKey: formatDateKey(normalizedActiveDate),
    systemDateKey: formatDateKey(systemDate),
    resolvedSchedule,
    sequences: Array.isArray(sequences) ? sequences : [],
    runnableSequences,
    triggers: resolution.triggers,
    events,
    nextEvents,
    activeEvents,
    blockers: buildSequenceBlockers({
      sequences: Array.isArray(sequences) ? sequences : [],
      classes,
      resolvedSchedule,
      userSettings,
      systemDate,
      scheduleRuntimeMode,
    }),
  };
};

export const createSequenceRuntimeHistoryEntry = ({
  event,
  status,
  reason = "",
  timestamp = new Date(),
  details = {},
}) => ({
  id: `${event?.key || "sequence-event"}|${status}|${toIso(timestamp)}`,
  eventKey: event?.key || "",
  dateKey: event?.dateKey || "",
  sequenceId: event?.sequenceId || "",
  sequenceName: event?.sequenceName || "Automated routine",
  stepId: event?.stepId || "",
  stepTitle: event?.title || event?.type || "Step",
  stepType: event?.type || "",
  classId: event?.classId || "",
  className: event?.className || "",
  status,
  reason,
  timestamp: toIso(timestamp),
  scheduledStartTime: toIso(event?.startTime),
  anchorTime: toIso(event?.anchorTime),
  details,
});

export const trimSequenceRuntimeHistory = (history = []) =>
  history.slice(0, SEQUENCE_RUNTIME_HISTORY_LIMIT);

export const isActiveDateToday = (activeDate) =>
  Math.abs(buildDateFromActiveDate(activeDate).getTime() - buildDateFromActiveDate(new Date()).getTime()) < DAY_MS &&
  formatDateKey(activeDate) === formatDateKey(new Date());
